import { v4 as uuidv4 } from 'uuid';
import { create } from 'zustand';
import useChatStore from './chatStore';
import useUserStore from './userStore';

export const SLIDING_MS = 8000;
export const MAX_MS = 25000;

const STORAGE_KEY = 'outbound_message_buffers';

export type OutboundPlatform = 'wa' | 'messaging';

export interface BufferEntry {
  trackId: string;
  botId: string;
  sessionId: string;
  platform: OutboundPlatform;
  channel?: string;
  text: string;
  phase: 'buffering';
  startedAt: number;
  deadlineAt: number;
  slidingMs: number;
  maxMs: number;
}

interface PersistedBuffers {
  buffers: Record<string, BufferEntry>;
}

interface OutboundBufferState {
  buffers: Record<string, BufferEntry>;
  timerIds: Record<string, ReturnType<typeof setTimeout>>;
  enqueue: (params: {
    sessionId: string;
    botId: string;
    platform: OutboundPlatform;
    channel?: string;
    text: string;
  }) => void;
  flush: (sessionId: string) => Promise<void>;
  flushNow: (sessionId: string) => void;
  flushAll: () => void;
  restoreAndFlush: () => void;
  getBufferByTrackId: (trackId: string) => BufferEntry | null;
  getBufferProgress: (trackId: string) => number;
  persistBuffers: () => void;
}

function isGroupingEnabled(): boolean {
  const config = useUserStore.getState().currentBot?.settings?.config ?? {};
  return config.human_message_grouping === true;
}

export function isHumanSendPipelineEnabled(): boolean {
  return isGroupingEnabled();
}

const readPersisted = (): PersistedBuffers | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedBuffers;
    if (!parsed?.buffers || typeof parsed.buffers !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
};

const writePersisted = (buffers: Record<string, BufferEntry>): void => {
  if (typeof window === 'undefined') return;
  try {
    const entries = Object.values(buffers);
    if (entries.length === 0) {
      window.localStorage.removeItem(STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ buffers }));
  } catch {
    // ignore quota / privacy errors
  }
};

const useOutboundBufferStore = create<OutboundBufferState>((set, get) => {
  const clearTimer = (sessionId: string) => {
    const { timerIds } = get();
    const existing = timerIds[sessionId];
    if (existing) {
      clearTimeout(existing);
      set((state) => {
        const next = { ...state.timerIds };
        delete next[sessionId];
        return { timerIds: next };
      });
    }
  };

  const scheduleFlush = (sessionId: string, delayMs: number) => {
    clearTimer(sessionId);
    const timerId = setTimeout(() => {
      get().flush(sessionId).catch(() => {
        // errors handled inside flush / dispatch
      });
    }, delayMs);
    set((state) => ({
      timerIds: { ...state.timerIds, [sessionId]: timerId },
    }));
  };

  const removeBuffer = (sessionId: string) => {
    clearTimer(sessionId);
    set((state) => {
      const nextBuffers = { ...state.buffers };
      delete nextBuffers[sessionId];
      writePersisted(nextBuffers);
      return { buffers: nextBuffers };
    });
  };

  return {
    buffers: {},
    timerIds: {},

    persistBuffers: () => {
      writePersisted(get().buffers);
    },

    getBufferByTrackId: (trackId: string) => {
      const entry = Object.values(get().buffers).find((b) => b.trackId === trackId);
      return entry ?? null;
    },

    getBufferProgress: (trackId: string) => {
      const entry = get().getBufferByTrackId(trackId);
      if (!entry) return 0;
      const now = Date.now();
      const elapsed = now - entry.startedAt;
      const total = entry.deadlineAt - entry.startedAt;
      if (total <= 0) return 1;
      return Math.min(1, Math.max(0, elapsed / total));
    },

    enqueue: ({
      sessionId, botId, platform, channel, text,
    }) => {
      const grouping = isGroupingEnabled();
      const chatStore = useChatStore.getState();
      const now = Date.now();
      const existing = get().buffers[sessionId];

      if (grouping) {
        if (!existing) {
          const trackId = uuidv4();
          const entry: BufferEntry = {
            trackId,
            botId,
            sessionId,
            platform,
            channel,
            text,
            phase: 'buffering',
            startedAt: now,
            deadlineAt: now + MAX_MS,
            slidingMs: SLIDING_MS,
            maxMs: MAX_MS,
          };

          chatStore.addOptimisticMessage(trackId, text, 'buffering');

          set((state) => {
            const nextBuffers = { ...state.buffers, [sessionId]: entry };
            writePersisted(nextBuffers);
            return { buffers: nextBuffers };
          });

          scheduleFlush(sessionId, SLIDING_MS);
          return;
        }

        const mergedText = `${existing.text}\n${text}`;
        const nextDeadline = existing.deadlineAt;
        const flushAt = Math.min(now + SLIDING_MS, nextDeadline);
        const delayMs = Math.max(0, flushAt - now);

        chatStore.updateOptimisticMessage(existing.trackId, { text: mergedText });

        set((state) => {
          const nextBuffers = {
            ...state.buffers,
            [sessionId]: {
              ...existing,
              text: mergedText,
              phase: 'buffering' as const,
            },
          };
          writePersisted(nextBuffers);
          return { buffers: nextBuffers };
        });

        scheduleFlush(sessionId, delayMs);
        return;
      }

      // Grouping OFF: send right away
      const trackId = uuidv4();
      const entry: BufferEntry = {
        trackId,
        botId,
        sessionId,
        platform,
        channel,
        text,
        phase: 'buffering',
        startedAt: now,
        deadlineAt: now,
        slidingMs: 0,
        maxMs: 0,
      };

      chatStore.addOptimisticMessage(trackId, text, 'buffering');

      set((state) => {
        const nextBuffers = { ...state.buffers, [sessionId]: entry };
        writePersisted(nextBuffers);
        return { buffers: nextBuffers };
      });

      Promise.resolve().then(() => {
        get().flush(sessionId).catch(() => {});
      });
    },

    flush: async (sessionId: string) => {
      const entry = get().buffers[sessionId];
      if (!entry) return;

      // Remove the buffer immediately so any new messages arriving during the async
      // send are treated as fresh entries rather than merging into this flush.
      clearTimer(sessionId);
      set((state) => {
        const nextBuffers = { ...state.buffers };
        delete nextBuffers[sessionId];
        writePersisted(nextBuffers);
        return { buffers: nextBuffers };
      });

      const chatStore = useChatStore.getState();
      const textToSend = entry.text;

      chatStore.updateOptimisticMessage(entry.trackId, { localStatus: 'sending' });

      try {
        if (entry.platform === 'wa') {
          await chatStore.dispatchMessage(entry.botId, textToSend, entry.trackId);
        } else {
          await chatStore.dispatchInstagramMessage(
            entry.sessionId,
            textToSend,
            entry.trackId,
            entry.channel,
          );
        }
      } catch {
        // dispatch already marks error on the optimistic message
      }
    },

    flushNow: (sessionId: string) => {
      get().flush(sessionId).catch(() => {});
    },

    flushAll: () => {
      const sessionIds = Object.keys(get().buffers);
      sessionIds.forEach((sessionId) => {
        get().flush(sessionId).catch(() => {});
      });
    },

    restoreAndFlush: () => {
      const persisted = readPersisted();
      if (!persisted?.buffers) return;

      const restored = persisted.buffers;
      if (Object.keys(restored).length === 0) return;

      set({ buffers: restored });

      Object.keys(restored).forEach((sessionId) => {
        Promise.resolve().then(() => {
          get().flush(sessionId).catch(() => {});
        });
      });
    },
  };
});

export function initOutboundBufferStore(): void {
  if (typeof window === 'undefined') return;

  useOutboundBufferStore.getState().restoreAndFlush();

  window.addEventListener('beforeunload', () => {
    useOutboundBufferStore.getState().persistBuffers();
  });
}

export default useOutboundBufferStore;
